<template>
    <div :class="['tuner-page', { 'tuner-embedded': embedded }]">
        <BackendHeader v-if="!embedded" />

        <div class="at-container">
            <div class="at-header">
                <h2>Audience Tuner — MommyDeals</h2>
                <div class="at-stats" v-if="stats">
                    <span class="at-stat"><strong>{{ stats.labeled }}</strong> labeled</span>
                    <span class="at-stat" v-if="stats.avgError != null">avg error <strong>{{ stats.avgError.toFixed(0) }}</strong> pts</span>
                    <span class="at-stat" v-if="stats.avgHumanScore != null">human avg <strong>{{ stats.avgHumanScore.toFixed(0) }}</strong></span>
                    <span class="at-stat" v-if="stats.avgModelScore != null">model avg <strong>{{ stats.avgModelScore.toFixed(0) }}</strong></span>
                    <button @click="loadBatch" class="at-btn at-btn-sm">Refresh</button>
                </div>
            </div>

            <p class="at-instructions">
                Rate each deal: <strong>Is this a good deal for a mom running a household?</strong>
                Score 10–100 with keys <kbd>1</kbd>–<kbd>9</kbd> (×10) or <kbd>0</kbd> for 100.
                Press <kbd>-</kbd> (or click <strong>✕</strong>) for bad-data / not a real product. <kbd>N</kbd> to skip.
            </p>

            <!-- Architecture B: two stacked bars. Label model trains directly
                 from operator labels (AudienceTunerLabels) and is the primary
                 driver right now. Clicky/niche model is CTR-derived from
                 ClickyDealClicks + HistoricalDeals; until HistoricalDeals is
                 bootstrapped its bar shows 0 ready and is disabled. At
                 inference, app.py blends both 70/30 and falls back to label-
                 only when the Clicky model isn't loaded. -->
            <TrainNowBar
                kind="label"
                title="Label model (your scores)"
                :status="trainingStatus?.labelModel"
                :loading="trainingStatusLoading"
                :triggering="trainingTriggering === 'label'"
                @train="onTrainClick('label')"
                @refresh="loadTrainingStatus"
            />
            <TrainNowBar
                kind="niche"
                title="Clicky model (CTR-driven)"
                :status="trainingStatus?.nicheModel"
                :loading="trainingStatusLoading"
                :triggering="trainingTriggering === 'niche'"
                :disabled="(trainingStatus?.nicheModel?.labelsReady ?? 0) === 0"
                :disabled-note="(trainingStatus?.nicheModel?.labelsReady ?? 0) === 0 ? 'HistoricalDeals not yet bootstrapped' : undefined"
                @train="onTrainClick('niche')"
                @refresh="loadTrainingStatus"
            />

            <div v-if="loading" class="at-loading">Loading deals…</div>
            <div v-else-if="done" class="at-done">No more deals to label. Come back later!</div>

            <template v-else>
                <!-- Bulk-action bar: appears whenever 1+ deals are checked. Apply
                     one score to all selected deals in a single round-trip. -->
                <div v-if="selectedIds.size > 0" class="at-bulk-bar">
                    <span class="at-bulk-label">
                        <strong>{{ selectedIds.size }}</strong> selected —
                        apply score:
                    </span>
                    <button
                        v-for="s in scoreSteps"
                        :key="s"
                        :class="['at-score-btn', scoreClass(s)]"
                        @click="bulkSubmitScore(s)"
                        :disabled="bulkSubmitting"
                        :title="`Apply score ${s} to all selected`"
                    >{{ s }}</button>
                    <button class="at-score-btn at-skip"
                            @click="bulkSubmitScore(0)"
                            :disabled="bulkSubmitting"
                            title="Mark all selected as bad data">✕</button>
                    <button class="at-bulk-clear" @click="selectedIds.clear()">Clear</button>
                </div>

                <div class="at-grid">
                    <div v-for="(deal, idx) in deals" :key="deal.dealId"
                         :class="['at-card', { 'at-card-active': idx === activeIdx, 'at-card-done': labeled.has(deal.dealId), 'at-card-selected': selectedIds.has(deal.dealId) }]">
                        <input
                            v-if="!labeled.has(deal.dealId)"
                            type="checkbox"
                            class="at-card-check"
                            :checked="selectedIds.has(deal.dealId)"
                            @change="toggleSelect(deal.dealId, ($event.target as HTMLInputElement).checked)"
                            title="Select for bulk grading"
                        />
                        <div class="at-card-img">
                            <ImagePreview v-if="firstImage(deal)" :imageUrl="firstImage(deal)!" :maxWidth="120" :maxHeight="120" :finalWidth="400" :finalHeight="400" />
                            <div v-else class="at-no-img">no img</div>
                        </div>
                    <div class="at-card-info">
                        <div class="at-card-title">{{ truncate(deal.title, 80) }}</div>
                        <div class="at-card-meta">
                            <span v-if="deal.brand" class="at-meta-item">{{ deal.brand }}</span>
                            <span v-if="deal.category" class="at-meta-item at-meta-cat" :title="catCountTitle(deal.categoryLabelCount)">
                                {{ deal.category }}
                                <span class="at-cat-count" :class="catCountClass(deal.categoryLabelCount)">×{{ deal.categoryLabelCount }}</span>
                            </span>
                            <span v-if="deal.finalPrice" class="at-meta-item at-meta-price">${{ Number(deal.finalPrice).toFixed(2) }}</span>
                            <span v-if="deal.retailPrice && deal.retailPrice > deal.finalPrice" class="at-meta-item at-meta-retail">
                                <s>${{ Number(deal.retailPrice).toFixed(2) }}</s>
                            </span>
                            <span v-if="deal.promoCode" class="at-meta-item at-meta-promo">{{ deal.promoCode }}</span>
                            <span v-if="deal.effectiveGrade" class="at-meta-item" :class="`grade-${(deal.effectiveGrade||'').toLowerCase()}`">{{ deal.effectiveGrade }}</span>
                            <span v-if="deal.source" class="at-meta-item at-meta-source" :title="`Source: ${deal.source}`">{{ truncate(deal.source, 28) }}</span>
                            <span v-if="deal.asin" class="at-meta-item at-meta-asin" :title="`ASIN: ${deal.asin}`">{{ deal.asin }}</span>
                        </div>
                        <div class="at-card-scores">
                            <span class="at-model-score">Model: <strong>{{ deal.modelScore }}</strong></span>
                            <a v-if="deal.productLink" :href="deal.productLink" target="_blank" class="at-link">↗</a>
                        </div>
                    </div>
                    <div class="at-card-actions" v-if="!labeled.has(deal.dealId)">
                        <button
                            v-for="s in scoreSteps"
                            :key="s"
                            :class="['at-score-btn', scoreClass(s)]"
                            @click="submitScore(deal, s)"
                            :title="`Score ${s}`"
                        >{{ s }}</button>
                        <button class="at-score-btn at-skip" @click="submitScore(deal, 0)" title="Irrelevant / Bad data">✕</button>
                    </div>
                    <div class="at-card-labeled" v-else>✓ Labeled</div>
                </div>
            </div>
            </template>

            <div v-if="!done && !loading && deals.length > 0" class="at-footer">
                <span class="at-progress">{{ labeled.size }} / {{ deals.length }} labeled</span>
                <button @click="loadBatch" class="at-btn">Load Next Batch</button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, inject } from 'vue'
import { useRoute } from 'vue-router'
import BackendHeader from '@/components/BackendHeader.vue'
import ImagePreview from '@/components/Backend/ImagePreview.vue'
import TrainNowBar from '@/components/TrainNowBar.vue'
import { MODAL_CLOSE } from '@/services/modal'
import api from '@/api'

const props = defineProps<{ embedded?: boolean; dealId?: number }>()
const closeModal = inject(MODAL_CLOSE, undefined)
const route = useRoute()

interface AudienceDeal {
    dealId: number
    title: string | null
    brand: string | null
    finalPrice: number | null
    retailPrice: number | null
    imagesJson: string | null
    promoCode: string | null
    salesRank: number | null
    productLink: string | null
    source: string | null
    asin: string | null
    category: string | null
    categoryLabelCount: number
    modelScore: number
    modelConfidence: number
    effectiveGrade: string | null
}

const deals = ref<AudienceDeal[]>([])
const loading = ref(false)
const done = ref(false)
const activeIdx = ref(0)
const labeled = ref(new Set<number>())
const stats = ref<any>(null)

// Bulk-grading: deals checked for "apply same score to all of these".
// Cleared after a successful bulk submit, or manually via the "Clear" button.
const selectedIds = ref(new Set<number>())
const bulkSubmitting = ref(false)

// Training-now state. Polled on mount and after a triggered train, so the
// "X labels ready / last trained Nm ago" banner reflects reality.
// trainingTriggering holds which model is mid-trigger ('label' | 'niche')
// or null, so the two bars can disable independently.
type TrainKind = 'label' | 'niche'
const trainingStatus = ref<any>(null)
const trainingStatusLoading = ref(false)
const trainingTriggering = ref<TrainKind | null>(null)
let trainingPollTimer: ReturnType<typeof setInterval> | null = null

const loadTrainingStatus = async () => {
    trainingStatusLoading.value = true
    try {
        const resp = await api.getTrainingStatus()
        trainingStatus.value = resp.data
    } catch { /* keep last value */ }
    finally { trainingStatusLoading.value = false }
}

const onTrainClick = async (kind: TrainKind) => {
    if (trainingTriggering.value) return
    trainingTriggering.value = kind
    try {
        if (kind === 'label') await api.trainLabelModel()
        else await api.trainNicheModel()
        // Poll every 5s while training so the banner flips back to "completed".
        if (trainingPollTimer) clearInterval(trainingPollTimer)
        const statusKey = kind === 'label' ? 'labelModel' : 'nicheModel'
        trainingPollTimer = setInterval(async () => {
            await loadTrainingStatus()
            if (trainingStatus.value?.[statusKey]?.status !== 'training') {
                if (trainingPollTimer) { clearInterval(trainingPollTimer); trainingPollTimer = null }
            }
        }, 5000)
        await loadTrainingStatus()
    } catch (e) {
        console.error('Trigger train failed', e)
    } finally {
        trainingTriggering.value = null
    }
}

const toggleSelect = (dealId: number, on: boolean) => {
    const next = new Set(selectedIds.value)
    if (on) next.add(dealId)
    else next.delete(dealId)
    selectedIds.value = next
}

const bulkSubmitScore = async (score: number) => {
    if (selectedIds.value.size === 0 || bulkSubmitting.value) return
    bulkSubmitting.value = true
    const ids = Array.from(selectedIds.value)
    try {
        await api.saveAudienceLabelBulk({ dealIds: ids, yourScore: score })
        // Mark all selected as labeled in-memory so the UI updates without reload.
        const next = new Set(labeled.value)
        for (const id of ids) next.add(id)
        labeled.value = next
        selectedIds.value = new Set()
        await loadStats()
    } catch (e) {
        console.error('Bulk audience label failed', e)
    } finally {
        bulkSubmitting.value = false
    }
}

const loadStats = async () => {
    try {
        const resp = await api.getAudienceTunerStats()
        stats.value = resp.data
    } catch { /* non-fatal */ }
}

const loadBatch = async () => {
    loading.value = true
    try {
        // Single-deal mode: fetch just the specific deal for correction
        if (props.dealId) {
            const resp = await api.getAudienceTunerDeal(props.dealId)
            if (resp.data.deal) {
                deals.value = [resp.data.deal]
                labeled.value = new Set()
                activeIdx.value = 0
                done.value = false
            } else {
                done.value = true
                deals.value = []
            }
            loading.value = false
            return
        }
        const resp = await api.getAudienceTunerBatch('mommydeals', 10)
        if (resp.data.done) {
            done.value = true
            deals.value = []
        } else {
            deals.value = resp.data.deals
            labeled.value = new Set()
            activeIdx.value = 0
            done.value = false
        }
    } catch {
        done.value = true
    } finally {
        loading.value = false
    }
}

const submitScore = async (deal: AudienceDeal, score: number) => {
    try {
        await api.saveAudienceLabel({ dealId: deal.dealId, yourScore: score })
        labeled.value = new Set([...labeled.value, deal.dealId])
        await loadStats()

        // Single-deal correction mode: auto-close modal after label save
        if (props.dealId && closeModal) {
            closeModal({ corrected: true, dealId: deal.dealId, score })
            return
        }

        if (labeled.value.size >= deals.value.length) {
            await loadBatch()
        } else {
            const next = deals.value.findIndex((d, i) => i > activeIdx.value && !labeled.value.has(d.dealId))
            if (next >= 0) activeIdx.value = next
        }
    } catch (e) {
        console.error('Failed to save audience label', e)
    }
}

const firstImage = (deal: AudienceDeal): string | null => {
    if (!deal.imagesJson) return null
    try {
        const arr = JSON.parse(deal.imagesJson)
        return Array.isArray(arr) && arr.length > 0 ? arr[0] : typeof arr === 'string' ? arr : null
    } catch { return null }
}

const truncate = (s: string | null, n: number) => s && s.length > n ? s.slice(0, n) + '…' : s || ''

// 10-step audience score: 10, 20, 30, ..., 100. The skip/bad-data button (✕)
// stays separate since "score 0 = not a real product" is a different signal
// from "score 10 = real product but low audience fit".
const scoreSteps = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100]
const scoreClass = (s: number): string => {
    if (s >= 80) return 'at-hot'
    if (s >= 60) return 'at-warm'
    if (s >= 40) return 'at-mild'
    if (s >= 20) return 'at-cool'
    return 'at-cold'
}

// Depth target per category. Below this, the per-category mean (the model's
// "category_affinity" feature) has too much SE to be useful; above it, the
// category contributes real signal. Mirrors MIN_SAMPLES_FOR_AFFINITY in
// MommyDeals.PricePy/niche_model.py + the tier cutoff in the sampler SQL.
const CATEGORY_DEPTH_TARGET = 15
const catCountClass = (n: number): string => {
    if (n === 0) return 'at-cat-count-zero'
    if (n < CATEGORY_DEPTH_TARGET) return 'at-cat-count-deepening'
    return 'at-cat-count-saturated'
}
const catCountTitle = (n: number): string => {
    if (n === 0) return 'No labels yet — opening new ground'
    if (n < CATEGORY_DEPTH_TARGET) return `${n}/${CATEGORY_DEPTH_TARGET} — needs ${CATEGORY_DEPTH_TARGET - n} more to be useful to the model`
    return `${n} labels — saturated`
}

const onKeyDown = (e: KeyboardEvent) => {
    if (loading.value || done.value || deals.value.length === 0) return
    const target = e.target as HTMLElement | null
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return

    const deal = deals.value[activeIdx.value]
    if (!deal || labeled.value.has(deal.dealId)) return

    // Keys 1-9 -> scores 10-90, key 0 -> 100, '-' -> 0 (bad data).
    // 'n' moves to the next unlabeled deal.
    if (/^[1-9]$/.test(e.key)) submitScore(deal, parseInt(e.key, 10) * 10)
    else if (e.key === '0') submitScore(deal, 100)
    else if (e.key === '-') submitScore(deal, 0)
    else if (e.key === 'n' || e.key === 'N') {
        const next = deals.value.findIndex((d, i) => i > activeIdx.value && !labeled.value.has(d.dealId))
        if (next >= 0) activeIdx.value = next
    }
}

onMounted(() => {
    loadStats()
    loadBatch()
    loadTrainingStatus()
    window.addEventListener('keydown', onKeyDown)
})
onBeforeUnmount(() => {
    window.removeEventListener('keydown', onKeyDown)
    if (trainingPollTimer) clearInterval(trainingPollTimer)
})
</script>

<style scoped>
.tuner-page { min-height: 100vh; background: #f5f5f5; padding: 20px; text-align: left; }
.tuner-embedded { min-height: unset; background: transparent; padding: 0; }
.at-container { max-width: 1200px; margin: 0 auto; background: #fff; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); padding: 20px; }
.tuner-embedded .at-container { box-shadow: none; border-radius: 0; max-width: none; }

.at-header { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 12px; padding-bottom: 12px; border-bottom: 2px solid #e0e0e0; flex-wrap: wrap; }
.at-header h2 { margin: 0; color: #333; font-size: 18px; }
.at-stats { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; font-size: 12px; color: #555; }
.at-stat { padding: 3px 10px; background: #eceff1; border-radius: 12px; }
.at-instructions { font-size: 13px; color: #666; margin: 0 0 16px 0; }
.at-instructions kbd { background: #eceff1; padding: 1px 5px; border-radius: 3px; font-size: 11px; }
.at-loading, .at-done { text-align: center; padding: 40px; color: #999; font-size: 14px; }

/* Bulk-action bar -- sticky so it stays in view while you scroll the grid. */
.at-bulk-bar {
    position: sticky;
    top: 0;
    z-index: 10;
    background: #fff8e1;
    border: 1px solid #ffd54f;
    border-radius: 6px;
    padding: 8px 12px;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: wrap;
}
.at-bulk-label { font-size: 12px; color: #6b4f00; margin-right: 6px; }
.at-bulk-clear { margin-left: 8px; padding: 4px 10px; background: none; border: 1px solid #cfd8dc; border-radius: 4px; font-size: 11px; color: #555; cursor: pointer; }
.at-bulk-clear:hover { background: #f5f5f5; }

.at-card-check { margin-right: 4px; cursor: pointer; }
.at-card-selected { background: #fffbe6; border-color: #ffd54f; }

.at-grid { display: flex; flex-direction: column; gap: 8px; }
.at-card { display: flex; align-items: center; gap: 12px; padding: 10px 14px; border: 2px solid #e0e0e0; border-radius: 8px; transition: border-color 0.15s; }
.at-card-active { border-color: #1565c0; background: #f8f9ff; padding: 16px 20px; }
/* Once labeled, drop the card from view entirely. The deal stays in the
   `deals` array (so the labeled count + nav indexing stay correct) -- it's
   just hidden so the operator's eye stays on what's left. */
.at-card-done { display: none; }

.at-card-img { flex-shrink: 0; width: 120px; height: 120px; }
.at-no-img { width: 120px; height: 120px; background: #f5f5f5; border-radius: 4px; display: flex; align-items: center; justify-content: center; font-size: 11px; color: #bbb; }

/* The currently-focused card gets a bigger image. ImagePreview locks its
   max-width/height at setup time from props, so we override via scoped
   :deep() to reach the inner <img> and force a larger size. */
.at-card-active .at-card-img,
.at-card-active .at-no-img { width: 440px; height: 440px; }
.at-card-active :deep(.imgPreview img) { max-width: 440px !important; max-height: 440px !important; }

.at-card-info { flex: 1; min-width: 0; }
.at-card-title { font-size: 12px; font-weight: 600; color: #333; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.at-card-meta { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 4px; font-size: 11px; color: #666; }
.at-meta-item { padding: 1px 6px; background: #f5f5f5; border-radius: 4px; }
.at-meta-cat { background: #e3f2fd; color: #1565c0; }
.at-cat-count { margin-left: 4px; padding: 0 4px; background: #1565c0; color: #fff; border-radius: 8px; font-size: 9px; font-weight: 700; }
.at-cat-count-zero       { background: #c62828; }  /* red:    no labels, opening new ground */
.at-cat-count-deepening  { background: #ef6c00; }  /* orange: 1-14, deepening toward useful */
.at-cat-count-saturated  { background: #2e7d32; }  /* green:  15+, contributes real signal */
.at-meta-price { background: #e8f5e9; color: #2e7d32; font-weight: 700; }
.at-meta-retail { color: #999; }
.at-meta-promo { background: #f3e5f5; color: #7b1fa2; }
.at-meta-source { background: #ede7f6; color: #4527a0; font-size: 10px; max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.at-meta-asin { background: #f1f8e9; color: #33691e; font-family: monospace; font-size: 10px; letter-spacing: 0.5px; }
.at-card-scores { display: flex; gap: 8px; align-items: center; margin-top: 4px; font-size: 11px; color: #888; }
.at-model-score strong { color: #333; }
.at-link { color: #1565c0; text-decoration: none; font-size: 14px; }

.at-card-actions { display: flex; gap: 4px; flex-shrink: 0; }
.at-score-btn { padding: 4px 8px; border: 1px solid #ddd; border-radius: 6px; font-size: 11px; cursor: pointer; transition: all 0.1s; background: #fff; }
.at-score-btn:hover { transform: scale(1.05); }
.at-hot { border-color: #2e7d32; color: #2e7d32; }
.at-hot:hover { background: #2e7d32; color: #fff; }
.at-warm { border-color: #1565c0; color: #1565c0; }
.at-warm:hover { background: #1565c0; color: #fff; }
.at-mild { border-color: #888; color: #888; }
.at-mild:hover { background: #888; color: #fff; }
.at-cool { border-color: #ef6c00; color: #ef6c00; }
.at-cool:hover { background: #ef6c00; color: #fff; }
.at-cold { border-color: #c62828; color: #c62828; }
.at-cold:hover { background: #c62828; color: #fff; }
.at-skip { border-color: #bbb; color: #bbb; font-size: 10px; padding: 4px 6px; }
.at-skip:hover { background: #eee; }

.at-card-labeled { flex-shrink: 0; color: #2e7d32; font-weight: 700; font-size: 12px; }
.at-footer { display: flex; justify-content: space-between; align-items: center; margin-top: 16px; padding-top: 12px; border-top: 1px solid #eee; }
.at-progress { font-size: 13px; color: #666; }
.at-btn { padding: 8px 16px; background: #1565c0; color: #fff; border: none; border-radius: 4px; cursor: pointer; font-size: 13px; }
.at-btn:hover { background: #0d47a1; }
.at-btn-sm { padding: 4px 10px; font-size: 11px; }

.grade-great { background: #2e7d32; color: #fff; border-radius: 10px; padding: 1px 6px; font-size: 10px; font-weight: 700; }
.grade-good { background: #66bb6a; color: #fff; border-radius: 10px; padding: 1px 6px; font-size: 10px; font-weight: 700; }
.grade-sale { background: #fff3c4; color: #8a6d00; border-radius: 10px; padding: 1px 6px; font-size: 10px; font-weight: 700; }
.grade-regular { background: #eceff1; color: #555; border-radius: 10px; padding: 1px 6px; font-size: 10px; font-weight: 700; }
.grade-notadeal { background: #d32f2f; color: #fff; border-radius: 10px; padding: 1px 6px; font-size: 10px; font-weight: 700; }
</style>
